import React, { useEffect, useState } from 'react';
import _ from 'lodash';
import { useRecoilValue, useRecoilState } from 'recoil';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import {
  DatePicker,
  Tabs,
  Table,
  Spin,
  Tag,
  Collapse,
  Divider,
  Segmented,
  Popover
} from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight } from '@fortawesome/pro-duotone-svg-icons';
import Button from '../../shared-components/buttons/Button';
import { H1 } from '../../shared-components/typography/Title';
import {
  userPrivilagesAtom,
  notificationsAtom,
  dashboardAnalyticsHeaderAtom,
  dashboardAnalyticsDataAtom,
  dashboardAnalyticsLoadedAtom,
  dashboardAnalyticsShouldLoadAtom
} from '../../../atoms/Atoms';
import api from '../../../api/api';
import Select from '../../shared-components/form/Select';
import envConfig from '../../../envConfig';
import { thousand, toLocalTime } from '../../../functions';
import MainPage from '../../shared-components/main-page/MainPage';
import AuthorityView from './shared-components/AuthorityView';
import ServiceLevelsView from './shared-components/ServiceLevelsView';
import ComplianceView from './shared-components/ComplianceView';

function Analytics() {
  const { t, i18n } = useTranslation();
  const { RangePicker } = DatePicker;
  const userPrivilages = useRecoilValue(userPrivilagesAtom);
  const [notifications, setNotifications] = useRecoilState(notificationsAtom);
  const [dashboardAnalyticsHeader, setDashboardAnalyticsHeader] = useRecoilState(dashboardAnalyticsHeaderAtom);
  const [dashboardAnalyticsData, setDashboardAnalyticsData] = useRecoilState(dashboardAnalyticsDataAtom);
  const [dashboardAnalyticsShouldLoad, setDashboardAnalyticsShouldLoad] = useRecoilState(dashboardAnalyticsShouldLoadAtom);
  const [loadingOperators, setLoadingOperators] = useState(false);
  const [loadingRoutes, setLoadingRoutes] = useState(false);
  const [currentTab, setCurrentTab] = useState('0');
  const [operators, setOperators] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [filteredRoutes, setFilteredRoutes] = useState({
    options: [{ key: 'any', value: '', text: t('any') }],
    optGroups: []
  });
  const [initialized, setInitialized] = useState(false);

  function getOperators() {
    setLoadingOperators(true);
    api.get('dashboard/operators')
    .then((response) => {
      setLoadingOperators(false);
      if (response.data.success) {
        setOperators([{ operatorId: '', operatorName: t('any') }, ...response.data.data]);
      } else {
        setNotifications([
          ...notifications,
          {
            title: t('error'),
            description: response.data.error,
            error: true,
          }
        ]);
      }
    })
    .catch((error) => {
      setLoadingOperators(false);
      setNotifications([
        ...notifications,
        {
          title: t('error'),
          description: error.message,
          error: true,
        }
      ]);
    });
  }

  function getRoutes() {
    setLoadingRoutes(true);
    api.get('dashboard/routes')
    .then((response) => {
      setLoadingRoutes(false);
      if (response.data.success) {
        setRoutes(response.data.data);
        // setDashboardAnalyticsData({
        //   ...dashboardAnalyticsData,
        //   routes: response.data.data
        // });
      } else {
        setNotifications([
          ...notifications,
          {
            title: t('error'),
            description: response.data.error,
            error: true,
          }
        ]);
      }
    })
    .catch((error) => {
      setLoadingRoutes(false);
      setNotifications([
        ...notifications,
        {
          title: t('error'),
          description: error.message,
          error: true,
        }
      ]);
    });
  }

  function getData(selectedTabIndex) {
    if (selectedTabIndex === '0') {
      setDashboardAnalyticsShouldLoad({
        ...dashboardAnalyticsShouldLoad,
        page1: true
      });
    } else if (selectedTabIndex === '1') {
            setDashboardAnalyticsShouldLoad({
        ...dashboardAnalyticsShouldLoad,
        page2: true
      });
    } else if (selectedTabIndex === '2') {
            setDashboardAnalyticsShouldLoad({
        ...dashboardAnalyticsShouldLoad,
        page3: true
      });
    }
  }

  function filterRoutes() {
    if (dashboardAnalyticsData.routes && initialized) {
      let tempRoutes = [...dashboardAnalyticsData.routes];
      if (dashboardAnalyticsHeader.operatorId?.length > 0) {
        tempRoutes = _.filter(dashboardAnalyticsData.routes, (route) => route.operatorId === dashboardAnalyticsHeader.operatorId);
      }
      const grouped = _.groupBy(tempRoutes, (route) => route.operatorId);
      const tempGroups = Object.keys(grouped).map(function (key) {
        const operatorFiltered = _.filter(operators, (o) => o.operatorId === key);
        return {
          name: operatorFiltered.length > 0 ? operatorFiltered[0].operatorName : key,
          options: _.map(grouped[key], (v) => ({
            key: `route-${v.routeId}`,
            value: v.routeId,
            text: v.routeName,
          }))
        }
      });
      const newFilteredRoutes = {
        ...filteredRoutes,
        optGroups: tempGroups
      };
      setFilteredRoutes(newFilteredRoutes);
    }
  }

  useState(() => {
    setDashboardAnalyticsHeader({
      ...dashboardAnalyticsHeader,
      routeId: null,
      operatorId: null,
      // operatorId: 'TD',
      start: dayjs().startOf('week').format('MMM DD, YYYY'),
      end: dayjs().endOf('week').format('MMM DD, YYYY'),
    });
    getOperators();
    setTimeout(() => {
      getRoutes();
      if (currentTab !== '0') {
        setCurrentTab('0');
      } else {
        getData(0);
      }
      setInitialized(true);
    }, 500);
  }, []);

  useEffect(() => {
    filterRoutes();
  }, [dashboardAnalyticsData.routes, initialized]);

  useEffect(() => {
    filterRoutes();
    if (dashboardAnalyticsHeader.routeId !== '') {
      setDashboardAnalyticsHeader({
        ...dashboardAnalyticsHeader,
        routeId: ''
      });
    }
  }, [dashboardAnalyticsHeader.operatorId]);

  useEffect(() => {
    getData(currentTab);
  }, [currentTab]);

  useEffect(() => {
    setDashboardAnalyticsData({
      ...dashboardAnalyticsData,
      operators
    });
  }, [operators]);

  useEffect(() => {
    setDashboardAnalyticsData({
      ...dashboardAnalyticsData,
      routes
    });
  }, [routes]);

  return (
    <MainPage>
      <H1 text={t('dashboard_analytics')} />
      <div className="grid gap-2 grid-cols-1 md:grid-cols-2 lg:grid-cols-7">
        <Select
          className="col-span-1 lg:col-start-3"
          label={t('operator')}
          id="operator"
          name="operator"
          disabled={loadingOperators || dashboardAnalyticsHeader.loading}
          // disabled
          value={dashboardAnalyticsHeader?.operatorId}
          onChange={(obj) => {
            setDashboardAnalyticsHeader({
              ...dashboardAnalyticsHeader,
              operatorId: obj.target.value
            });
          }}
          options={_.map(operators, (operator) => ({
            key: `operator-${operator.operatorId}`,
            value: operator.operatorId,
            text: operator.operatorName
          }))}
        />
        <Select
          className="col-span-1"
          label={t('route')}
          id="route"
          name="route"
          disabled={loadingRoutes || dashboardAnalyticsHeader.loading}
          value={dashboardAnalyticsHeader?.routeId}
          onChange={(obj) => {
            setDashboardAnalyticsHeader({
              ...dashboardAnalyticsHeader,
              routeId: obj.target.value
            });
          }}
          options={filteredRoutes.options}
          optGroups={filteredRoutes.optGroups}
        />
        <RangePicker
          value={[
            dayjs(dashboardAnalyticsHeader?.start),
            dayjs(dashboardAnalyticsHeader?.end)
          ]}
          disabled={dashboardAnalyticsHeader.loading}
          className="h-11 bg-transparent col-span-1 md:col-span-2 lg:col-span-2 md:mt-1"
          onChange={(obj) => {
            setDashboardAnalyticsHeader({
              ...dashboardAnalyticsHeader,
              start: obj[0],
              end: obj[1]
            });
          }}
          format="MMM DD, YYYY"
        />
        <Button
          className="bg-primaryColor mt-1 px-2 col-span-1 md:col-span-2 lg:col-span-1"
          textColor="text-primaryTextColor"
          loaderColor="bg-primaryTextColor"
          borderColor="border-primaryBorderColor"
          text={t('refresh')}
          loading={dashboardAnalyticsHeader.loading}
          onClick={() => getData(currentTab)}
        />
      </div>
      <Tabs activeKey={currentTab} onChange={(key) => setCurrentTab(key)}>
        <Tabs.TabPane key="0" tab={t('authority_overview')}>
            <AuthorityView />
        </Tabs.TabPane>
        <Tabs.TabPane key="1" tab={t('service_levels_reliability')}>
            <ServiceLevelsView />
        </Tabs.TabPane>
        {/* <Tabs.TabPane key="2" tab={t('compliance_reporting')}>
            <ComplianceView />
        </Tabs.TabPane> */}
      </Tabs>
    </MainPage>
  );
}

export default Analytics;
